import React, { useRef, useEffect, useState, useCallback } from "react";
import ReactApexChart from "react-apexcharts";

/**
 * WorkforceBarChart
 *
 * Grouped bar chart (Female + Male) across two categories.
 * Features:
 *   1. No count numbers shown on/inside/above the bars (as requested)
 *   2. Percentage label + hollow teal circle at the TOP of each bar
 *      - Female labels are shifted left, Male labels shifted right to prevent overlap
 *      - Lines, dots, and percentage labels respect the legend hide/show toggle
 *   3. Teal connector lines: Female[0]→Female[1] and Male[0]→Male[1]
 *   4. Dual y-axis (left = count, right = 0–100%), properly assigned
 *   5. Native robust tooltip configuration that displays count + percentage cleanly
 */
const WorkforceBarChart = ({
  categories = [],
  seriesData = [],
  percentageData = { female: [], male: [] },
  colors = ["#F48FB1", "#06513D"],
}) => {
  const chartRef = useRef(null);
  const [overlay, setOverlay] = useState({
    lines: [],
    dotLabels: [],
  });
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0, top: 0, left: 0 });

  // ── Helper to calculate nice rounded Y-axis max ──────────────────────────
  const getNiceMax = (rawVal) => {
    if (rawVal <= 0) return 100;
    const headroom = rawVal * 1.3; // 30% headroom for label placement above bars
    const power = Math.pow(10, Math.floor(Math.log10(headroom)));
    const ratio = headroom / power;
    let niceRatio;
    if (ratio <= 1.2) niceRatio = 1.2;
    else if (ratio <= 1.5) niceRatio = 1.5;
    else if (ratio <= 2) niceRatio = 2;
    else if (ratio <= 3) niceRatio = 3;
    else if (ratio <= 4) niceRatio = 4;
    else if (ratio <= 5) niceRatio = 5;
    else if (ratio <= 6) niceRatio = 6;
    else if (ratio <= 8) niceRatio = 8;
    else niceRatio = 10;

    return Math.ceil(niceRatio * power);
  };

  const allCounts = seriesData.flatMap((s) => s.data || []).filter(Boolean);
  const rawMax = allCounts.length ? Math.max(...allCounts) : 100;
  const yMax = getNiceMax(rawMax);
  const tickAmt = 10;

  const pctArrays = [percentageData.female || [], percentageData.male || []];

  // Compute the effective total from count/percentage pairs so the right
  // Y-axis shows real percentages that match the dot labels (e.g. 23.08%).
  let effectiveTotal = null;
  outer: for (let sIdx = 0; sIdx < seriesData.length; sIdx++) {
    const counts = seriesData[sIdx]?.data || [];
    const pcts = pctArrays[sIdx] || [];
    for (let i = 0; i < counts.length; i++) {
      const c = counts[i];
      const p = pcts[i];
      if (c > 0 && p > 0) {
        effectiveTotal = c / (p / 100);
        break outer;
      }
    }
  }

  // ── Walk the rendered SVG DOM to find bar positions ──────────────────────
  const computeOverlay = useCallback(() => {
    const wrapper = chartRef.current;
    if (!wrapper) return;

    const svgEl = wrapper.querySelector("svg.apexcharts-svg");
    if (!svgEl) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const svgRect = svgEl.getBoundingClientRect();
    if (!svgRect.width) return;

    const topOffset = svgRect.top - wrapperRect.top;
    const leftOffset = svgRect.left - wrapperRect.left;

    setSvgSize({
      w: svgRect.width,
      h: svgRect.height,
      top: topOffset,
      left: leftOffset,
    });

    // Query series groups. We match elements having class "apexcharts-series".
    const allSeriesGroups = Array.from(
      svgEl.querySelectorAll("g.apexcharts-series"),
    );

    const lines = [];
    const dotLabels = [];

    // Process the bar series.
    // Note: ApexCharts series groups align in order with seriesData.
    seriesData.forEach((seriesObj, sIdx) => {
      // Find the corresponding SVG series group by matching class/index structure
      const seriesG = allSeriesGroups[sIdx];
      if (!seriesG) return;

      const bars = Array.from(seriesG.querySelectorAll(".apexcharts-bar-area"));
      if (!bars.length) return;

      // Check if the series is collapsed/hidden by checking its bounding width/height
      const firstBarRect = bars[0].getBoundingClientRect();
      const isCollapsed = firstBarRect.width === 0 || firstBarRect.height === 0;
      if (isCollapsed) return;

      const pts = bars.map((bar, rIdx) => {
        const r = bar.getBoundingClientRect();
        return {
          cx: r.left - svgRect.left + r.width / 2,
          topY: r.top - svgRect.top,
          count: seriesObj.data?.[rIdx] ?? 0,
          pct: pctArrays[sIdx]?.[rIdx],
        };
      });

      // Hollow dot + percentage text at bar top
      pts.forEach((pt) => {
        dotLabels.push({
          x: pt.cx,
          y: pt.topY,
          pct: pt.pct,
          sIdx: sIdx,
        });
      });

      // Connector line between the two bars in this series
      if (pts.length >= 2) {
        lines.push({
          x1: pts[0].cx,
          y1: pts[0].topY,
          x2: pts[1].cx,
          y2: pts[1].topY,
        });
      }
    });

    setOverlay({ lines, dotLabels });
  }, [seriesData, percentageData]);

  // Use ResizeObserver to dynamically update coordinates on any container size change
  useEffect(() => {
    const wrapper = chartRef.current;
    if (!wrapper) return;

    // Initial timeout to ensure chart is mounted and elements are ready
    const t = setTimeout(computeOverlay, 300);

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(computeOverlay);
    });
    observer.observe(wrapper);

    return () => {
      clearTimeout(t);
      observer.disconnect();
    };
  }, [computeOverlay]);

  const femaleSeriesName = seriesData[0]?.name || "Female";
  const maleSeriesName = seriesData[1]?.name || "Male";

  // ── ApexCharts options ────────────────────────────────────────────────────
  const chartOptions = {
    chart: {
      height: 400,
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false }, // disable animations to get correct DOM measurements immediately
      events: {
        mounted: () => setTimeout(computeOverlay, 200),
        // Recalculate overlay lines when a legend item is clicked to toggle series visibility
        legendClick: () => setTimeout(computeOverlay, 150),
      },
    },
    colors,
    plotOptions: {
      bar: {
        columnWidth: "40%",
        distributed: false,
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent", "transparent"],
    },
    xaxis: {
      categories,
      axisBorder: { show: true, color: "#000" },
      labels: {
        show: true,
        rotate: 0, // disable automatic rotation of labels
        rotateAlways: false,
        hideOverlappingLabels: false, // ensure all labels are shown
        trim: false,
        minHeight: 45, // give enough vertical room for multi-line labels
        style: {
          fontSize: "12px",
          fontFamily: "Inter, sans-serif",
        },
      },
    },
    yaxis: [
      // Left axis — count (visible) for Female series
      {
        seriesName: femaleSeriesName,
        axisTicks: { show: true },
        axisBorder: { show: true, color: "#000" },
        min: 0,
        max: yMax,
        tickAmount: tickAmt,
        labels: { formatter: (v) => Math.round(v) },
      },
      // Right axis — percentage display (mapped to Male series to ensure correct 1:1 legend colors)
      // Since it shares the exact same min & max scale as the Left axis, both bars align perfectly.
      {
        seriesName: maleSeriesName,
        opposite: true,
        axisTicks: { show: true },
        axisBorder: { show: true, color: "#000" },
        min: 0,
        max: yMax,
        tickAmount: tickAmt,
        labels: {
          formatter: (v) => {
            const total = effectiveTotal || yMax || 100;
            const pct = (v / total) * 100;
            return `${Math.round(pct)}%`;
          },
        },
      },
    ],
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      markers: { radius: 3 },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    // Standard robust native tooltip formatter
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val, { seriesIndex, dataPointIndex }) => {
          if (
            dataPointIndex === undefined ||
            dataPointIndex === null ||
            dataPointIndex < 0
          ) {
            return val;
          }
          const pct = pctArrays[seriesIndex]?.[dataPointIndex];
          return pct !== undefined && pct !== null ? `${val} (${pct}%)` : val;
        },
      },
    },
    grid: {
      borderColor: "#e7e7e7",
      padding: {
        bottom: 15, // extra padding at the bottom for multi-line x-axis labels
      },
    },
  };

  const LINE_COLOR = "#00ACC1"; // teal – matches reference image

  return (
    <div ref={chartRef} style={{ position: "relative" }}>
      <ReactApexChart
        options={chartOptions}
        series={seriesData}
        type="bar"
        height={400}
      />

      {/* ── SVG overlay: connector lines, dots, labels ────────── */}
      {svgSize.w > 0 && (
        <svg
          style={{
            position: "absolute",
            top: `${svgSize.top}px`,
            left: `${svgSize.left}px`,
            width: `${svgSize.w}px`,
            height: `${svgSize.h}px`,
            pointerEvents: "none", // never block chart mouse events / tooltip
            overflow: "visible",
          }}
        >
          {/* 1. Teal connector lines (drawn first, under dots) */}
          {overlay.lines.map((ln, i) => (
            <line
              key={`line-${i}`}
              x1={ln.x1}
              y1={ln.y1}
              x2={ln.x2}
              y2={ln.y2}
              stroke={LINE_COLOR}
              strokeWidth={2}
              strokeLinecap="round"
              style={{ pointerEvents: "none" }}
            />
          ))}

          {/* 2. Hollow dots + percentage labels at each bar top */}
          {overlay.dotLabels.map((dl, i) => {
            // Shift female label left, male label right to prevent overlap
            const isFemale = dl.sIdx === 0;
            const textOffsetX = isFemale ? -8 : 8;
            const textAnchor = isFemale ? "end" : "start";

            return (
              <g key={`dot-${i}`} style={{ pointerEvents: "none" }}>
                {/* Outer white fill + teal stroke = hollow look */}
                <circle
                  cx={dl.x}
                  cy={dl.y}
                  r={5}
                  fill="#fff"
                  stroke={LINE_COLOR}
                  strokeWidth={2}
                  style={{ pointerEvents: "none" }}
                />
                {/* Percentage text */}
                {dl.pct != null && (
                  <text
                    x={dl.x + textOffsetX}
                    y={dl.y - 4}
                    textAnchor={textAnchor}
                    fontSize="11"
                    fontWeight="700"
                    fill="#333"
                    fontFamily="Inter, sans-serif"
                    style={{ pointerEvents: "none" }}
                  >
                    {dl.pct}%
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
};

export default WorkforceBarChart;
